import { type FC } from 'react';

import { IconButton } from '../Button';
import type { IconButtonSize } from '../Button/types';
import ArrowLeftIcon from './assets/arrow-left-icon.svg';
import ArrowRightIcon from './assets/arrow-right-icon.svg';
import { StyledEllipsis, StyledPageButton, StyledPaginationRoot } from './Pagination.styled';
import type { PaginationProps, PaginationSize } from './types';

const NAV_ICON_BUTTON_SIZE: Record<PaginationSize, IconButtonSize> = {
  md: 'sm',
  sm: 'xs',
};

// ---------------------------------------------------------------------------
// Pagination range builder
// ---------------------------------------------------------------------------

type PaginationItem = number | 'start-ellipsis' | 'end-ellipsis';

// Fixed 7-slot window
function buildPaginationRange(page: number, count: number): PaginationItem[] {
  if (count <= 7) {
    return Array.from({ length: count }, (_, i) => i + 1);
  }

  if (page <= 4) {
    return [1, 2, 3, 4, 5, 'end-ellipsis', count];
  }

  if (page >= count - 3) {
    return [1, 'start-ellipsis', count - 4, count - 3, count - 2, count - 1, count];
  }

  return [1, 'start-ellipsis', page - 1, page, page + 1, 'end-ellipsis', count];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const defaultProps = {
  showPrevNext: true,
  disabled: false,
  size: 'md',
} as const satisfies Pick<PaginationProps, 'showPrevNext' | 'disabled' | 'size'>;

const Pagination: FC<PaginationProps> = ({
  page,
  count,
  onChange,
  disabled = defaultProps.disabled,
  showPrevNext = defaultProps.showPrevNext,
  size = defaultProps.size,
}) => {
  const items = buildPaginationRange(page, count);

  const handlePageChange = (newPage: number) => {
    if (disabled || newPage < 1 || newPage > count || newPage === page) return;
    onChange(newPage);
  };

  return (
    <StyledPaginationRoot role="navigation" aria-label="Pagination">
      {showPrevNext && (
        <li>
          <IconButton
            color="tertiary"
            variant="outline"
            size={NAV_ICON_BUTTON_SIZE[size]}
            disabled={disabled || page === 1}
            onClick={() => handlePageChange(page - 1)}
            aria-label="Go to previous page"
          >
            <ArrowLeftIcon />
          </IconButton>
        </li>
      )}

      {items.map((item, index) => {
        if (item === 'start-ellipsis' || item === 'end-ellipsis') {
          return (
            <li key={item}>
              <StyledEllipsis aria-hidden="true" disabled={disabled} $size={size}>
                ...
              </StyledEllipsis>
            </li>
          );
        }

        return (
          <li key={`${item}-${index}`}>
            <StyledPageButton
              $active={item === page}
              $paginationSize={size}
              disabled={disabled}
              onClick={() => handlePageChange(item)}
              aria-label={`Go to page ${item}`}
              aria-current={item === page ? 'page' : undefined}
            >
              {item}
            </StyledPageButton>
          </li>
        );
      })}

      {showPrevNext && (
        <li>
          <IconButton
            color="tertiary"
            variant="outline"
            size={NAV_ICON_BUTTON_SIZE[size]}
            disabled={disabled || page === count}
            onClick={() => handlePageChange(page + 1)}
            aria-label="Go to next page"
          >
            <ArrowRightIcon />
          </IconButton>
        </li>
      )}
    </StyledPaginationRoot>
  );
};

Pagination.displayName = 'Pagination';

export default Pagination;
